import {
  type AgencySettingName,
  type GlobalSettingName,
  type SettingName,
  isAgencySettingsCode,
  isGlobalSettingsCode
} from '@gat-ship/types'

/**
 * Plain snapshot of every resolved setting — the data the provider holds in its
 * `ref`. The pure lookups read from this snapshot; they never touch the `ref`.
 */
export interface GlobalSettingsSnapshot {
  agencySettingsMap: Record<
    string,
    Record<AgencySettingName, boolean | number | string>
  >
  settings: Partial<Record<GlobalSettingName, boolean | number | string>>
}

/** Resolve an agency-scoped setting, falling back to `defaultValue`. */
export function lookupAgencySetting(
  snapshot: Readonly<GlobalSettingsSnapshot>,
  params: {
    setting: AgencySettingName
    agencyId: string
    defaultValue: boolean | number | string | undefined
  }
): boolean | number | string | undefined {
  return (
    snapshot.agencySettingsMap[params.agencyId]?.[params.setting] ??
    params.defaultValue
  )
}

/** Resolve a global-scoped setting, falling back to `defaultValue`. */
export function lookupGlobalSetting(
  snapshot: Readonly<GlobalSettingsSnapshot>,
  params: {
    setting: GlobalSettingName
    defaultValue: boolean | number | string | undefined
  }
): boolean | number | string | undefined {
  return snapshot.settings[params.setting] ?? params.defaultValue
}

/**
 * Decide which scope a setting belongs to and resolve it. Returns the decision
 * as data — `kind: 'unknown'` when the code matches neither scope — so the
 * shell can perform the unknown-setting `console.warn` (a side effect) itself.
 */
export function resolveSetting(
  snapshot: Readonly<GlobalSettingsSnapshot>,
  params: {
    setting: SettingName
    agencyId?: string
    defaultValue: boolean | number | string | undefined
  }
): {
  kind: 'agency' | 'global' | 'unknown'
  value: boolean | number | string | undefined
} {
  const { setting, agencyId, defaultValue } = params
  if (agencyId && isAgencySettingsCode(setting)) {
    return {
      kind: 'agency',
      value: lookupAgencySetting(snapshot, { setting, agencyId, defaultValue })
    }
  }
  if (isGlobalSettingsCode(setting)) {
    return {
      kind: 'global',
      value: lookupGlobalSetting(snapshot, { setting, defaultValue })
    }
  }
  return { kind: 'unknown', value: defaultValue }
}

/**
 * Decide the next settings snapshot for an initialization context. Returns
 * `null` when the context calls for no change, so the shell only reassigns when
 * there is a new snapshot — preserving the original conditional-assign
 * behaviour (the dormant hardcoded user/database-id branch).
 */
export function decideInitialSnapshot(contexts: {
  userId: string
  databaseId?: string
}): GlobalSettingsSnapshot | null {
  if (
    contexts.userId === 'f1f41237-228a-440d-9677-c73921b72cc7' &&
    contexts.databaseId === 'ce807f30-5a57-4b49-b58d-63420a98dc70'
  ) {
    return {
      agencySettingsMap: {},
      settings: {}
    }
  }
  return null
}
